import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DollarSign, AlertTriangle, Clock, X, CreditCard, CheckCircle2, RefreshCw, Plus, TrendingUp, Calendar as CalendarIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  listarContratos,
  buscarContrato,
  registrarPagamento,
  listarParcelasVencidas,
  listarMensalidades,
  buscarMetricas,
  buscarCronograma,
  Contrato,
  Parcela,
  MensalidadeItem,
} from "../services/financeiro";
import { listarProjetos } from "../services/projetos";

const MODALIDADE_LABELS: Record<string, string> = {
  unico: "Pagamento Único",
  parcelado: "Parcelado",
  mensalidade: "Mensalidade",
  setup_mensalidade: "Setup + Mensalidade",
};
const STATUS_COLORS: Record<string, string> = { pendente: "bg-amber-100 text-amber-700", parcial: "bg-blue-100 text-blue-700", pago: "bg-green-100 text-green-700", atrasado: "bg-red-100 text-red-700" };

export function Financeiro() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [sel, setSel] = useState<string | null>(null);
  const [pay, setPay] = useState<Parcela | null>(null);
  const [showEscolherProjeto, setShowEscolherProjeto] = useState(false);

  const { data: cData } = useQuery({ queryKey: ["contratos"], queryFn: () => listarContratos() });
  const { data: vencidas } = useQuery({ queryKey: ["parcelas-vencidas"], queryFn: () => listarParcelasVencidas() });
  const { data: mensalidades } = useQuery({ queryKey: ["mensalidades"], queryFn: () => listarMensalidades() });
  const { data: detalhe } = useQuery({ queryKey: ["contrato", sel], queryFn: () => buscarContrato(sel!), enabled: !!sel });
  const { data: metricas } = useQuery({ queryKey: ["financeiro-metricas"], queryFn: buscarMetricas, enabled: can(["gestor", "admin"]) });
  const { data: cronograma } = useQuery({ queryKey: ["financeiro-cronograma"], queryFn: () => buscarCronograma(12), enabled: can(["gestor", "admin"]) });

  const payM = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { dataPagamento: string; valorPago: number; formaPagamento: string } }) => registrarPagamento(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contrato"] });
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["mensalidades"] });
      qc.invalidateQueries({ queryKey: ["parcelas-vencidas"] });
      setPay(null);
    }
  });

  const contratos: Contrato[] = (cData as any)?.contratos || [];
  const totalPendente = contratos.filter((c: Contrato) => ["pendente","parcial","atrasado"].includes(c.status)).reduce((s, c) => s + (c.saldoRestante || 0), 0);
  const totalPago = contratos.reduce((s: number, c: Contrato) => s + (c.valorPago || 0), 0);
  const podeOperar = can(["gestor", "admin"]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Financeiro</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Visão consolidada de todos os projetos.
          </p>
        </div>
        {podeOperar && (
          <button
            onClick={() => setShowEscolherProjeto(true)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Plus size={16} /> Novo contrato
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={DollarSign} color="bg-green-100 text-green-600" label="Total Recebido" value={`R$ ${(metricas?.totalRecebido ?? totalPago).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
        <StatCard icon={Clock} color="bg-amber-100 text-amber-600" label="Pendente" value={`R$ ${(metricas?.totalPendente ?? totalPendente).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
        <StatCard icon={AlertTriangle} color="bg-red-100 text-red-600" label="Atrasado" value={`R$ ${(metricas?.totalAtrasado ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
        <StatCard icon={CalendarIcon} color="bg-blue-100 text-blue-600" label="A receber 30d" value={`R$ ${(metricas?.aReceber30d ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
        <StatCard icon={TrendingUp} color="bg-purple-100 text-purple-600" label={`MRR (${metricas?.mensalidadesAtivas ?? 0})`} value={`R$ ${(metricas?.mrr ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
      </div>

      {/* Cronograma de recebimentos */}
      {cronograma && cronograma.length > 0 && (
        <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <CalendarIcon size={18} className="text-blue-600" /> Cronograma de recebimentos
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Próximos 12 meses — agrupado por mês de vencimento</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-slate-500">Mês</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-500">A receber</th>
                  <th className="px-4 py-2 text-center font-medium text-slate-500">Itens</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-500">Detalhes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cronograma.map((m) => {
                  const [ano, mesNum] = m.mes.split("-");
                  const nomeMes = new Date(parseInt(ano), parseInt(mesNum) - 1, 1).toLocaleDateString("pt-BR", { month: "short", year: "numeric" });
                  return (
                    <tr key={m.mes} className="hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium capitalize">{nomeMes}</td>
                      <td className="px-4 py-2 text-right font-semibold text-slate-800">R$ {m.total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-2 text-center text-slate-500">{m.itens.length}</td>
                      <td className="px-4 py-2 text-xs text-slate-500 truncate max-w-md">
                        {m.itens.slice(0, 3).map((i) => `${i.projeto.nome} R$${i.valor.toFixed(0)}`).join(" · ")}
                        {m.itens.length > 3 && ` · +${m.itens.length - 3}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Mensalidades — visão "em dia / atrasado" */}
      <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <RefreshCw size={18} className="text-blue-600" /> Mensalidades
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Cobranças recorrentes — selo mostra se cliente está em dia.</p>
          </div>
          <span className="text-xs text-slate-500">{mensalidades?.length || 0} contratos</span>
        </div>
        {(!mensalidades || mensalidades.length === 0) && (
          <div className="px-6 py-8 text-center text-slate-400 text-sm">
            Nenhuma mensalidade ativa ainda. Use "Novo contrato" e escolha modalidade Mensalidade.
          </div>
        )}
        <div className="divide-y divide-slate-100">
          {mensalidades?.map((m) => (
            <MensalidadeRow
              key={m.id}
              m={m}
              podeOperar={podeOperar}
              onPagar={() => {
                if (!m.proximaParcelaId) return;
                setPay({
                  id: m.proximaParcelaId,
                  numero: 0,
                  valor: m.valorMensal,
                  vencimento: m.proximaParcela || new Date().toISOString(),
                  status: "pendente",
                  dataPagamento: null,
                  valorPago: null,
                  formaPagamento: null,
                });
              }}
            />
          ))}
        </div>
      </section>

      {/* Vencidas */}
      {vencidas && vencidas.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <h3 className="font-semibold text-red-700 mb-2 flex items-center gap-2"><AlertTriangle size={18} /> Parcelas Vencidas</h3>
          <div className="divide-y divide-red-100">
            {vencidas.map((p: any) => (
              <div key={p.id} className="flex items-center justify-between py-2">
                <span className="text-sm"><strong>{p.contrato?.projeto?.nome}</strong> — Parcela {p.numero} — R$ {Number(p.valor).toFixed(2)} — Venc. {new Date(p.vencimento).toLocaleDateString("pt-BR")}</span>
                {podeOperar && <button onClick={() => setPay(p)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded transition-colors">Registrar Pgto</button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contratos */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Contratos</h2>
          <p className="text-xs text-slate-500 mt-0.5">Clique no projeto para gerenciar parcelas e pagamentos.</p>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50"><tr>
            <th className="px-4 py-3 text-left font-medium text-slate-500">Projeto</th>
            <th className="px-4 py-3 text-left font-medium text-slate-500">Modalidade</th>
            <th className="px-4 py-3 text-right font-medium text-slate-500">Valor Total</th>
            <th className="px-4 py-3 text-right font-medium text-slate-500">Pago</th>
            <th className="px-4 py-3 text-right font-medium text-slate-500">Restante</th>
            <th className="px-4 py-3 text-center font-medium text-slate-500">Status</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {contratos.map(c => (
              <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium">
                  <a href={`/projetos/${c.projetoId}`} className="text-blue-600 hover:underline">{c.projeto?.nome}</a>
                </td>
                <td className="px-4 py-3">{MODALIDADE_LABELS[c.modalidade]}</td>
                <td className="px-4 py-3 text-right">{Number(c.valorTotal).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                <td className="px-4 py-3 text-right text-green-600 font-medium">{Number(c.valorPago || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                <td className="px-4 py-3 text-right font-semibold">{Number(c.saldoRestante || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[c.status]}`}>{c.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <a href={`/projetos/${c.projetoId}`} className="text-xs text-blue-600 hover:text-blue-700 font-medium">abrir →</a>
                </td>
              </tr>
            ))}
            {contratos.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Nenhum contrato. Crie um abrindo um projeto e usando o bloco Financeiro.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {/* Detalhe */}
      {detalhe && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold truncate pr-2">{detalhe.projeto.nome} — Parcelas</h3>
            <button onClick={() => setSel(null)} className="text-slate-400 hover:text-slate-600 shrink-0"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 text-sm">
            <div><p className="text-slate-500">Modalidade</p><p className="font-medium">{MODALIDADE_LABELS[detalhe.modalidade]}</p></div>
            <div><p className="text-slate-500">Valor Total</p><p className="font-medium">R$ {Number(detalhe.valorTotal).toFixed(2)}</p></div>
            <div><p className="text-slate-500">Saldo Restante</p><p className="font-bold text-slate-800">R$ {(detalhe.saldoRestante || 0).toFixed(2)}</p></div>
            {detalhe.valorSetup != null && detalhe.valorSetup > 0 && (
              <div><p className="text-slate-500">Setup</p><p className="font-medium">R$ {Number(detalhe.valorSetup).toFixed(2)}</p></div>
            )}
            {detalhe.mensalidade && (
              <div><p className="text-slate-500">Mensalidade</p><p className="font-medium">R$ {Number(detalhe.mensalidade.valorMensal).toFixed(2)}/mês</p></div>
            )}
            {detalhe.contratoAssinadoEm && (
              <div className="sm:col-span-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mt-1">
                <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1">
                  <CheckCircle2 size={12} /> Contrato assinado em {new Date(detalhe.contratoAssinadoEm).toLocaleDateString("pt-BR")}
                </p>
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-slate-50"><tr>
              <th className="px-3 py-2 text-left font-medium text-slate-500">#</th>
              <th className="px-3 py-2 text-left font-medium text-slate-500">Vencimento</th>
              <th className="px-3 py-2 text-right font-medium text-slate-500">Valor</th>
              <th className="px-3 py-2 text-right font-medium text-slate-500">Pago</th>
              <th className="px-3 py-2 text-center font-medium text-slate-500">Status</th>
              <th className="px-3 py-2 text-center font-medium text-slate-500">Ação</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {detalhe.parcelas?.map(p => {
                const isVencida = p.status === "pendente" && new Date(p.vencimento) < new Date();
                return (
                  <tr key={p.id} className={isVencida ? "bg-red-50" : ""}>
                    <td className="px-3 py-2">{p.numero}</td>
                    <td className="px-3 py-2">{new Date(p.vencimento).toLocaleDateString("pt-BR")}</td>
                    <td className="px-3 py-2 text-right">{Number(p.valor).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">{p.valorPago ? Number(p.valorPago).toFixed(2) : "—"}</td>
                    <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[p.status]}`}>{p.status}</span></td>
                    <td className="px-3 py-2 text-center">
                      {podeOperar && p.status !== "pago" && (
                        <button onClick={() => setPay(p)} className="text-blue-600 hover:text-blue-700 text-xs font-medium">Pagar</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Modal pagamento */}
      {pay && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 sm:p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><CreditCard size={20} /> Registrar Pagamento</h3>
            <p className="text-sm text-slate-500 mb-4">{pay.numero ? `Parcela ${pay.numero} — ` : ""}Valor: R$ {Number(pay.valor).toFixed(2)} — Venc. {new Date(pay.vencimento).toLocaleDateString("pt-BR")}</p>
            <form onSubmit={e => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              payM.mutate({ id: pay.id, data: { dataPagamento: fd.get("data") as string, valorPago: parseFloat(fd.get("valor") as string), formaPagamento: fd.get("forma") as string } });
            }} className="space-y-3">
              <div><label className="text-sm text-slate-500">Data do pagamento</label><input name="data" type="date" required defaultValue={new Date().toISOString().split("T")[0]} className="w-full px-3 py-2 rounded-lg border border-slate-300" /></div>
              <div><label className="text-sm text-slate-500">Valor pago</label><input name="valor" type="number" step="0.01" required defaultValue={Number(pay.valor)} className="w-full px-3 py-2 rounded-lg border border-slate-300" /></div>
              <div><label className="text-sm text-slate-500">Forma</label>
                <select name="forma" required className="w-full px-3 py-2 rounded-lg border border-slate-300">
                  <option value="pix">PIX</option><option value="boleto">Boleto</option><option value="cartao">Cartão</option><option value="ted">TED</option><option value="outro">Outro</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={payM.isPending} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium">{payM.isPending ? "Registrando..." : "Confirmar"}</button>
                <button type="button" onClick={() => setPay(null)} className="px-4 py-2.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEscolherProjeto && podeOperar && (
        <EscolherProjetoModal
          contratos={contratos}
          onClose={() => setShowEscolherProjeto(false)}
          onSelecionar={(projetoId) => {
            setShowEscolherProjeto(false);
            navigate(`/projetos/${projetoId}?criarContrato=1`);
          }}
        />
      )}
    </div>
  );
}

function EscolherProjetoModal({ contratos, onClose, onSelecionar }: {
  contratos: Contrato[];
  onClose: () => void;
  onSelecionar: (projetoId: string) => void;
}) {
  const [busca, setBusca] = useState("");
  const { data: projetosData } = useQuery({ queryKey: ["projetos-list-novo-contrato"], queryFn: () => listarProjetos({ perPage: 200 }) });
  const todos: Array<{ id: string; nome: string; cliente: string }> = (projetosData as any)?.projetos || [];

  // Projetos sem contrato ainda
  const projetosIdsComContrato = new Set(contratos.map((c) => c.projetoId));
  const semContrato = todos.filter((p) => !projetosIdsComContrato.has(p.id));
  const filtrados = semContrato.filter((p) => {
    const t = busca.trim().toLowerCase();
    if (!t) return true;
    return p.nome.toLowerCase().includes(t) || p.cliente.toLowerCase().includes(t);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800">Novo contrato — escolha o projeto</h3>
            <p className="text-xs text-slate-500 mt-0.5">Mostra apenas projetos sem contrato ainda.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="px-4 pt-3">
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar projeto ou cliente…"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {filtrados.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-8 px-3">
              {semContrato.length === 0
                ? "Todos os projetos já têm contrato."
                : "Nenhum projeto encontrado com esse filtro."}
            </p>
          )}
          {filtrados.map((p) => (
            <button
              key={p.id}
              onClick={() => onSelecionar(p.id)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 rounded-lg flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{p.nome}</p>
                <p className="text-[11px] text-slate-500 truncate">{p.cliente}</p>
              </div>
              <span className="text-xs text-blue-600">→</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MensalidadeRow({ m, podeOperar, onPagar }: { m: MensalidadeItem; podeOperar: boolean; onPagar: () => void }) {
  const selo =
    m.status === "em_dia"
      ? { txt: "EM DIA", cls: "bg-emerald-100 text-emerald-700" }
      : m.status === "vence_hoje"
      ? { txt: "VENCE HOJE", cls: "bg-amber-100 text-amber-700" }
      : m.status === "atrasada"
      ? { txt: `ATRASADA ${m.diasAtraso}d`, cls: "bg-red-100 text-red-700" }
      : m.status === "aguardando_definicao"
      ? { txt: "DIA NÃO DEFINIDO", cls: "bg-purple-100 text-purple-700" }
      : { txt: "SEM PARCELA", cls: "bg-slate-100 text-slate-500" };

  return (
    <div className="flex flex-wrap items-center px-4 sm:px-6 py-3 hover:bg-slate-50 transition-colors gap-3 sm:gap-4">
      <div className="flex-1 min-w-[160px]">
        <p className="font-semibold text-slate-800 truncate">{m.projeto.cliente}</p>
        <p className="text-xs text-slate-500 truncate">{m.projeto.nome}</p>
      </div>
      <div className="text-right text-sm shrink-0">
        <p className="font-mono text-slate-700">R$ {Number(m.valorMensal).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/mês</p>
        <p className="text-[11px] text-slate-500">
          {m.diaVencimento ? `dia ${m.diaVencimento}` : "sem dia definido"}
          {m.proximaParcela && ` · próx. ${new Date(m.proximaParcela).toLocaleDateString("pt-BR")}`}
        </p>
      </div>
      <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded shrink-0 ${selo.cls}`}>{selo.txt}</span>
      {podeOperar && m.proximaParcelaId && (
        <button
          onClick={onPagar}
          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded flex items-center gap-1 shrink-0"
          title="Registrar pagamento da próxima parcela"
        >
          <CheckCircle2 size={12} /> Pagar
        </button>
      )}
    </div>
  );
}


function StatCard({ icon: Icon, color, label, value }: { icon: any; color: string; label: string; value: string }) {
  // Reduz fonte conforme tamanho do valor pra não quebrar linha
  const tam = value.length <= 10
    ? "text-2xl"
    : value.length <= 13
      ? "text-xl"
      : value.length <= 16
        ? "text-lg"
        : value.length <= 19
          ? "text-base"
          : "text-sm";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center shrink-0`}><Icon size={20} /></div>
        <div className="min-w-0 flex-1">
          <p
            className={`font-bold text-slate-800 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis ${tam}`}
            title={value}
          >
            {value}
          </p>
          <p className="text-sm text-slate-500 truncate">{label}</p>
        </div>
      </div>
    </div>
  );
}
